import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ZoeaLogo from '@/components/ZoeaLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZOEA_MARK_URL = '/brand/zoea-origin-mark.svg';

// Branding per organisatie, gedeeld door alle tenant-shells. De sleutel is het
// organisatie-ID, zodat een tenantwissel nooit de branding van de vorige tenant
// toont en elke shell-navigatie geen nieuwe laadronde veroorzaakt.
const brandingCache = new Map();
const inflight = new Map();

const normalize = (data) => ({
  name: (data?.name || '').trim() || 'Zoea',
  logo_url: data?.logo_url || '',
});

const loadBranding = (organizationId) => {
  if (!inflight.has(organizationId)) {
    const request = axios.get(`${API}/organization/branding`)
      .then((response) => {
        const branding = normalize(response.data);
        brandingCache.set(organizationId, branding);
        return branding;
      })
      .finally(() => inflight.delete(organizationId));
    inflight.set(organizationId, request);
  }
  return inflight.get(organizationId);
};

/** Voor schermen die de branding wijzigen (Instellingen): houdt alle shells actueel. */
export const setOrganizationBranding = (organizationId, data) => {
  if (organizationId) brandingCache.set(organizationId, normalize(data));
};

export const resetOrganizationBrandingCache = () => {
  brandingCache.clear();
  inflight.clear();
};

/**
 * `platform`: geen tenantcontext (Zoea-branding), `loading`: tenant bekend maar
 * branding nog niet, `ready`: branding van de actieve tenant, `fallback`: ophalen
 * mislukt (Zoea-lockup, geen kapot beeld).
 */
export const useOrganizationBranding = () => {
  const { user } = useAuth();
  const organizationId = user?.organization_id || null;
  const [loaded, setLoaded] = useState({ organizationId: null, branding: null });

  useEffect(() => {
    if (!organizationId || brandingCache.has(organizationId)) return undefined;
    let active = true;
    loadBranding(organizationId)
      .then((branding) => active && setLoaded({ organizationId, branding }))
      .catch(() => active && setLoaded({ organizationId, branding: false }));
    return () => { active = false; };
  }, [organizationId]);

  if (!organizationId) return { status: 'platform' };
  const branding = brandingCache.get(organizationId)
    || (loaded.organizationId === organizationId ? loaded.branding : null);
  if (branding === false) return { status: 'fallback' };
  if (!branding) return { status: 'loading' };
  return { status: 'ready', ...branding };
};

/** Logo + naam van de actieve organisatie; Zoea-branding alleen zonder tenantcontext of als terugval. */
const OrganizationBrand = ({ className = '' }) => {
  const branding = useOrganizationBranding();
  const [failedLogo, setFailedLogo] = useState('');

  if (branding.status === 'platform' || branding.status === 'fallback') {
    return <ZoeaLogo className={`h-11 ${className}`} />;
  }
  if (branding.status === 'loading') {
    return <div className={`h-11 ${className}`} aria-busy="true" />;
  }

  const customLogo = branding.logo_url && branding.logo_url !== failedLogo ? branding.logo_url : '';
  if (!customLogo && branding.name.toLowerCase() === 'zoea') {
    return <ZoeaLogo className={`h-11 ${className}`} />;
  }
  return (
    <div className={`flex min-w-0 items-center gap-3 ${className}`}>
      <img
        src={customLogo || ZOEA_MARK_URL}
        alt={branding.name}
        onError={() => setFailedLogo(customLogo)}
        className="h-11 w-11 shrink-0 object-contain"
      />
      <span className="truncate text-lg font-bold text-slate-900 font-['Manrope']">{branding.name}</span>
    </div>
  );
};

export default OrganizationBrand;
